<script>
  import { createEventDispatcher } from 'svelte';
  import { Button, DataTable, Modal, Toolbar, ToolbarContent, ToolbarSearch, TooltipIcon } from 'carbon-components-svelte';
  import startCase from 'lodash/startCase';
  import { useNavigate } from 'svelte-navigator';

  import Json from 'carbon-icons-svelte/lib/Json16';

  import CollectionValueCell from './CollectionValueCell.svelte';

  import { toCsv, getDatedCsvFilename } from '../../shared/csv';
  import { downloadCsvStringAsFile } from '../utils';

  const ACTION_COLUMN_KEY = '__actions__';

  export let open = false,
    modalHeading = 'Entities',
    modalLabel = '',
    rows = [],
    keysToExclude = ['id'],
    exportable = false,
    /**
     * @type {((row: Record<string, unknown>) => string) | null}
     */
    createActionLink = null;

  const navigate = useNavigate();
  const dispatch = createEventDispatcher();

  let searchText = '';

  function stringifyCellValue(value) {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    return value;
  }

  function stringifyObjectValue(value) {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  function getColumnLabel(key) {
    return startCase(String(key).replace(/[_.]/g, ' '));
  }

  function cellText(value) {
    if (value === null || value === undefined) {
      return '';
    }
    if (Array.isArray(value)) {
      return value.map(cellText).join(' ');
    }
    if (typeof value === 'object') {
      return stringifyObjectValue(value);
    }
    return String(value);
  }

  function rowMatches(row, query) {
    return Object.values(row).some((value) => cellText(value).toLocaleLowerCase().includes(query));
  }

  function handleSearchInput(event) {
    searchText = event.target?.value?.trim() ?? '';
  }

  function clickAction(event, row) {
    const href = createActionLink(row);
    if (!href) {
      return;
    }

    event.preventDefault();
    navigate(href);
  }

  function onModalClose() {
    dispatch('close');
  }

  function exportCsv() {
    const columns = columnKeys.map((key) => ({ header: getColumnLabel(key), path: key }));
    const namePart = [modalHeading, modalLabel]
      .filter(Boolean)
      .join(' ')
      .replace(/[^\w.-]+/g, '_');
    downloadCsvStringAsFile(getDatedCsvFilename(namePart), toCsv(filteredRows, columns));
  }

  $: excludedKeysSet = new Set([...keysToExclude, ACTION_COLUMN_KEY]);
  $: columnKeys = [...new Set(rows.flatMap((row) => Object.keys(row)))].filter((key) => !excludedKeysSet.has(key));
  $: tableHeaders = [
    ...columnKeys.map((key) => ({ key, value: getColumnLabel(key) })),
    ...(createActionLink ? [{ key: ACTION_COLUMN_KEY, value: '', sort: false }] : []),
  ];
  $: filteredRows = searchText ? rows.filter((row) => rowMatches(row, searchText.toLocaleLowerCase())) : rows;
</script>

<Modal bind:open {modalHeading} {modalLabel} passiveModal preventCloseOnClickOutside size="lg" on:close={onModalClose}>
  <DataTable headers={tableHeaders} rows={filteredRows} sortable>
    <span slot="cell" let:cell let:row>
      {#if cell.key === ACTION_COLUMN_KEY}
        <Button kind="ghost" size="small" on:click={(event) => clickAction(event, row)}>View</Button>
      {:else if Array.isArray(cell.value)}
        <CollectionValueCell value={JSON.stringify(cell.value)} />
      {:else if cell.value !== null && typeof cell.value === 'object'}
        <TooltipIcon tooltipText={stringifyObjectValue(cell.value)} direction="top" align="start" icon={Json} portalTooltip />
      {:else}
        {stringifyCellValue(cell.value)}
      {/if}
    </span>
    <div class="toolbar-container">
      <Toolbar>
        <ToolbarContent>
          <ToolbarSearch placeholder="Search" persistent value={searchText} on:clear={handleSearchInput} on:input={handleSearchInput} />
          {#if exportable}
            <Button kind="ghost" size="small" disabled={filteredRows.length === 0} on:click={exportCsv}>Export CSV</Button>
          {/if}
        </ToolbarContent>
      </Toolbar>
    </div>
  </DataTable>
  {#if filteredRows.length === 0}
    <div class="empty-state">No results found.</div>
  {/if}
</Modal>

<style>
  .toolbar-container {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .empty-state {
    align-content: center;
    background-color: var(--cds-layer);
    color: var(--cds-text-secondary);
    height: var(--cds-spacing-09);
    text-align: center;
  }
</style>
